import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FileQuestion, Upload, CheckCircle2, Clock, Plus, Loader2 } from "lucide-react";

export function DocumentRequests({ caseId, isAttorney }) {
  const [requests, setRequests] = useState([]);
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState("");
  const fileRefs = useRef({});

  const load = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/document-requests`); setRequests(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const createReq = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/document-requests`, { label: label.trim(), description: description.trim() });
      toast.success("Document request sent to client");
      setLabel(""); setDescription(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fulfill = async (req, file) => {
    if (!file) return;
    setUploadingId(req.request_id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "Police");
      const up = await api.post(`/cases/${caseId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/cases/${caseId}/document-requests/${req.request_id}/fulfill`, { evidence_id: up.data.evidence_id });
      toast.success("Document uploaded — your attorney has been notified");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setUploadingId(""); }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="document-requests">
      <div className="flex items-center gap-2 mb-4">
        <FileQuestion className="w-5 h-5 text-cobalt" />
        <h3 className="font-heading font-semibold text-navy">Requested Documents</h3>
      </div>

      {isAttorney && (
        <div className="mb-4 space-y-2 border-b border-slate-100 pb-4">
          <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="docreq-label" placeholder="Document name (e.g. Signed medical authorization)"
            className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
          <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="docreq-desc" placeholder="Optional note for the client"
            className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
          <button onClick={createReq} disabled={busy || !label.trim()} data-testid="docreq-send"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Request Document
          </button>
        </div>
      )}

      {requests.length === 0 ? (
        <p className="text-sm text-slate-400">{isAttorney ? "No documents requested yet." : "Your attorney hasn't requested any documents."}</p>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.request_id} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0" data-testid={`docreq-${r.request_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-navy truncate">{r.label}</div>
                {r.description && <div className="text-xs text-slate-500 truncate">{r.description}</div>}
              </div>
              {r.status === "fulfilled" ? (
                <span className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Uploaded</span>
              ) : isAttorney ? (
                <span className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-600 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Pending</span>
              ) : (
                <>
                  <input type="file" ref={(el) => (fileRefs.current[r.request_id] = el)} onChange={(e) => fulfill(r, e.target.files?.[0])} className="hidden" />
                  <button onClick={() => fileRefs.current[r.request_id]?.click()} disabled={uploadingId === r.request_id} data-testid={`docreq-upload-${r.request_id}`}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cobalt text-white text-xs font-medium hover:bg-cobalt-hover transition-all active:scale-95">
                    {uploadingId === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
